import React from "react";
import { LucideIcon } from "lucide-react";
import { NeuralNodeCore } from "./NeuralNodeCore";
import { NeuralNodeLabel } from "./NeuralNodeLabel";
import { NeuralNodeDetails } from "./NeuralNodeDetails";
import { useNeuralNodeInteraction } from "./hooks/useNeuralNodeInteraction";
import { getNeuralNodeSizes } from "./utils/neuralNodeSizes";

interface NeuralNodeProps {
  agent: {
    icon: LucideIcon;
    title: string;
    description: string;
    clinicianBenefit: string;
    patientBenefit: string;
    category: string;
    color: string;
    featured?: boolean;
  };
  position: { x: number; y: number };
  index: number;
  onHover: (index: number | null) => void;
  isConnected: boolean;
  size: 'central' | 'large' | 'medium' | 'small';
}

export const NeuralNode: React.FC<NeuralNodeProps> = ({
  agent,
  position,
  index,
  onHover,
  isConnected,
  size
}) => {
  const {
    showDetails,
    isActive,
    isMobile,
    isInteractive: baseInteractive,
    handleMouseEnter,
    handleMouseLeave,
    handleTouch,
    handleTouchStart,
    handleTouchEnd,
  } = useNeuralNodeInteraction(index, onHover);

  const { sizeClasses, iconSizes } = getNeuralNodeSizes(isMobile);
  const isInteractive = baseInteractive || isConnected;

  return (
    <div
      className="absolute cursor-pointer group touch-manipulation"
      style={{
        left: `${position.x}%`,
        top: `${position.y}%`,
        transform: 'translate(-50%, -50%)',
        zIndex: showDetails ? 50 : isInteractive ? 30 : 20
      }}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onClick={handleTouch}
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
    >
      <NeuralNodeCore
        agent={agent}
        size={size}
        sizeClasses={sizeClasses}
        iconSizes={iconSizes}
        isInteractive={isInteractive}
        isActive={isActive}
        isMobile={isMobile}
      />
      
      <NeuralNodeLabel
        agent={agent}
        isInteractive={isInteractive}
        isMobile={isMobile}
      />
      
      {showDetails && !isMobile && (
        <NeuralNodeDetails
          agent={agent}
          isMobile={isMobile}
          onClose={handleTouch}
        />
      )}
    </div>
  );
};